// Listado de disposiciones emitidas/borrador para consulta de RRHH.
// Permite filtrar por año/tipo/estado, ver el escrito generado y descargar PDF.

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Chip, IconButton, Tooltip,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { nodeApiPath } from '../../utils/paths';

const FIRMA_SERVICE_URL = process.env.REACT_APP_FIRMA_SERVICE_URL || 'http://127.0.0.1:5850';

const PRIMARY = '#0277bd';
const PRIMARY_DARK = '#01579b';
const BR = '4px';

const fmtDate = (d) => { if (!d) return '-'; return new Date(d).toLocaleDateString('es-AR'); };

const estadoChip = (estado) => {
    const map = {
        BORRADOR: { bg: '#fff3e0', color: '#e65100' },
        EMITIDA: { bg: '#e8f5e9', color: '#2e7d32' },
        ANULADA: { bg: '#ffebee', color: '#c62828' },
    };
    const s = map[estado] || { bg: '#f5f5f5', color: '#666' };
    return <Chip label={estado} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.7rem', borderRadius: BR }} />;
};

const tipoChip = (tipo) => {
    const map = {
        VACACIONES: { bg: '#e0f2f1', color: '#00695c' },
        ART42: { bg: '#f3e5f5', color: '#6a1b9a' },
        LICENCIA: { bg: '#e3f2fd', color: '#1565c0' },
        CARTA_MEDICA: { bg: '#fff3e0', color: '#bf360c' },
        SANCION: { bg: '#ffebee', color: '#c62828' },
        OTROS: { bg: '#f5f5f5', color: '#666' },
    };
    const s = map[tipo] || { bg: '#f5f5f5', color: '#666' };
    return <Chip label={tipo} size="small" sx={{ bgcolor: s.bg, color: s.color, fontSize: '0.7rem', borderRadius: BR }} />;
};

export default function DisposicionesListado({ jf, notify }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
    const [filtroTipo, setFiltroTipo] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const [editDlg, setEditDlg] = useState({ open: false, disposicion: null, html: '', concepto: '', saving: false });
    const [anularDlg, setAnularDlg] = useState({ open: false, disposicion: null, motivo: '', saving: false });
    const [firmaDlg, setFirmaDlg] = useState({ open: false, disposicion: null, pin: '', saving: false, servicioOk: null });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            if (filtroAno) p.append('ano', String(filtroAno));
            if (filtroTipo) p.append('tipo', filtroTipo);
            if (filtroEstado) p.append('estado', filtroEstado);
            const r = await jf(`personal/disposiciones?${p}`);
            if (r.ok) setItems(r.items || []);
            else setItems([]);
        } catch { setItems([]); }
        setLoading(false);
    }, [filtroAno, filtroTipo, filtroEstado, jf]);

    useEffect(() => { cargar(); }, [cargar]);

    const verEscrito = async (d) => {
        try {
            const r = await jf(`personal/disposiciones/${d.Id}`);
            if (r.ok && r.disposicion?.Contenido_HTML) {
                const w = window.open('', '_blank');
                w.document.write(`<html><head><title>${d.Formato}</title><style>body{font-family:'Times New Roman',serif;max-width:800px;margin:30px auto;padding:20px}</style></head><body>${r.disposicion.Contenido_HTML}</body></html>`);
                w.document.close();
            } else notify?.('Sin contenido', 'warning');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
    };

    const abrirEdicion = async (d) => {
        try {
            const r = await jf(`personal/disposiciones/${d.Id}`);
            if (r.ok) setEditDlg({ open: true, disposicion: d, html: r.disposicion?.Contenido_HTML || '', concepto: r.disposicion?.Concepto || '', saving: false });
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
    };

    const guardarEdicion = async () => {
        setEditDlg(p => ({ ...p, saving: true }));
        try {
            const r = await jf(`personal/disposiciones/${editDlg.disposicion.Id}`, {
                method: 'PUT', body: JSON.stringify({ html: editDlg.html, concepto: editDlg.concepto }),
            });
            if (r.ok) { notify?.('Disposición actualizada', 'success'); setEditDlg({ open: false, disposicion: null, html: '', concepto: '', saving: false }); cargar(); }
            else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setEditDlg(p => ({ ...p, saving: false }));
    };

    const confirmarAnulacion = async () => {
        if (!anularDlg.motivo.trim()) { notify?.('Indicá motivo', 'warning'); return; }
        setAnularDlg(p => ({ ...p, saving: true }));
        try {
            const r = await jf(`personal/disposiciones/${anularDlg.disposicion.Id}/anular`, {
                method: 'POST', body: JSON.stringify({ motivo: anularDlg.motivo }),
            });
            if (r.ok) { notify?.('Disposición anulada', 'info'); setAnularDlg({ open: false, disposicion: null, motivo: '', saving: false }); cargar(); }
            else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setAnularDlg(p => ({ ...p, saving: false }));
    };

    const chequearServicioFirma = async () => {
        try {
            const r = await fetch(`${FIRMA_SERVICE_URL}/status`, { signal: AbortSignal.timeout(2000) });
            if (!r.ok) return false;
            // Que el cliente responda no alcanza: tiene que detectar el token.
            const d = await r.json().catch(() => ({}));
            return (d.tokensDetectados ?? 0) > 0;
        } catch { return false; }
    };

    const abrirFirma = async (d) => {
        const ok = await chequearServicioFirma();
        setFirmaDlg({ open: true, disposicion: d, pin: '', saving: false, servicioOk: ok });
    };

    const firmar = async () => {
        if (!firmaDlg.pin) { notify?.('Ingresá el PIN del token', 'warning'); return; }
        setFirmaDlg(p => ({ ...p, saving: true }));
        try {
            const d = firmaDlg.disposicion;
            const det = await jf(`personal/disposiciones/${d.Id}`);
            if (!det.ok || !det.disposicion?.Contenido_HTML) throw new Error('Sin contenido');
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            // 1. Generar PDF de la disposición
            const pdfRes = await fetch(nodeApiPath('pdf/from-html'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ html: det.disposicion.Contenido_HTML, filename: `Disposicion_${d.Formato}`, orientation: 'portrait' }),
            });
            if (!pdfRes.ok) throw new Error('Error generando PDF');
            const pdfBlob = await pdfRes.blob();

            // 2. Enviar al servicio local de firma
            const fd = new FormData();
            fd.append('archivo', pdfBlob, `${d.Formato}.pdf`);
            const firmaRes = await fetch(`${FIRMA_SERVICE_URL}/firmar?pin=${encodeURIComponent(firmaDlg.pin)}`, {
                method: 'POST', body: fd,
            });
            if (!firmaRes.ok) {
                const err = await firmaRes.json().catch(() => ({}));
                throw new Error(err.error || 'Error firmando');
            }
            const pdfFirmado = await firmaRes.blob();

            // 3. Subir el firmado al backend
            const fd2 = new FormData();
            fd2.append('file', pdfFirmado, `${d.Formato}_firmada.pdf`);
            const upRes = await fetch(nodeApiPath(`personal/disposiciones/${d.Id}/firmar`), {
                method: 'POST', body: fd2, headers: { Authorization: `Bearer ${token}` },
            });
            const r = await upRes.json();
            if (r.ok) { notify?.('Disposición firmada y guardada', 'success'); setFirmaDlg({ open: false, disposicion: null, pin: '', saving: false, servicioOk: null }); cargar(); }
            else throw new Error(r.mensaje || 'Error al guardar');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setFirmaDlg(p => ({ ...p, saving: false }));
    };

    const descargarFirmada = async (d) => {
        try {
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            const res = await fetch(nodeApiPath(`personal/disposiciones/${d.Id}/firmada.pdf`), { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { notify?.('Error descargando', 'error'); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `${d.Formato}_firmada.pdf`; document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
    };

    const descargarPDF = async (d) => {
        try {
            const r = await jf(`personal/disposiciones/${d.Id}`);
            if (!r.ok || !r.disposicion?.Contenido_HTML) { notify?.('Sin contenido', 'warning'); return; }
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            const res = await fetch(nodeApiPath('pdf/from-html'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ html: r.disposicion.Contenido_HTML, filename: `Disposicion_${d.Formato}`, orientation: 'portrait' }),
            });
            if (!res.ok) throw new Error('Error generando PDF');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `Disposicion_${d.Formato}.pdf`; document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
    };

    return (
        <Box>
            <Paper sx={{ p: 1.5, mb: 1, borderRadius: BR, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField label="Año" type="number" size="small" value={filtroAno}
                    onChange={e => setFiltroAno(parseInt(e.target.value) || '')}
                    InputLabelProps={{ shrink: true }} sx={{ width: 100 }} />
                <TextField label="Tipo" select size="small" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
                    SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ width: 160 }}>
                    <option value="">Todos</option>
                    <option value="VACACIONES">Vacaciones</option>
                    <option value="ART42">Art. 42</option>
                    <option value="LICENCIA">Licencia</option>
                    <option value="CARTA_MEDICA">Carta médica</option>
                    <option value="SANCION">Sanción</option>
                    <option value="OTROS">Otros</option>
                </TextField>
                <TextField label="Estado" select size="small" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
                    SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ width: 140 }}>
                    <option value="">Todos</option>
                    <option value="BORRADOR">Borrador</option>
                    <option value="EMITIDA">Emitida</option>
                    <option value="ANULADA">Anulada</option>
                </TextField>
                <Button variant="contained" size="small" startIcon={<SearchIcon />} onClick={cargar}
                    sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: PRIMARY_DARK } }}>Buscar</Button>
                <Typography variant="body2" sx={{ ml: 'auto', color: '#666' }}>{items.length} disposiciones</Typography>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: BR, border: '1px solid #e0e0e0', maxHeight: 'calc(100vh - 260px)' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa' }}>N° Disposición</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa' }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa' }}>Persona</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa' }}>Concepto</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa' }}>Creada</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa', width: 120, textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.length === 0 ? (
                                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#999' }}>Sin disposiciones</TableCell></TableRow>
                            ) : items.map(d => (
                                <TableRow key={d.Id} hover>
                                    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: PRIMARY }}>{d.Formato}</TableCell>
                                    <TableCell>{tipoChip(d.Tipo)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem' }}>{d.Apellido ? `${d.Apellido}, ${d.Nombre}` : '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem', color: '#666', maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.Concepto}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{fmtDate(d.Fecha)}</TableCell>
                                    <TableCell>{estadoChip(d.Estado)}</TableCell>
                                    <TableCell sx={{ textAlign: 'center', py: 0.5, whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Ver escrito"><IconButton size="small" onClick={() => verEscrito(d)} sx={{ color: PRIMARY }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Descargar PDF"><IconButton size="small" onClick={() => descargarPDF(d)} sx={{ color: '#c62828' }}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                                        {d.Estado === 'EMITIDA' && !d.Firmada && (
                                            <Tooltip title="Firmar digitalmente"><IconButton size="small" onClick={() => abrirFirma(d)} sx={{ color: '#2e7d32' }}><BorderColorIcon fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        {d.Firmada && (
                                            <Tooltip title={`Firmada por ${d.Firmada_Por} — Descargar`}>
                                                <IconButton size="small" onClick={() => descargarFirmada(d)} sx={{ color: '#2e7d32' }}>
                                                    <BorderColorIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {d.Estado === 'BORRADOR' && (
                                            <>
                                                <Tooltip title="Editar escrito"><IconButton size="small" onClick={() => abrirEdicion(d)} sx={{ color: '#e65100' }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                <Tooltip title="Anular"><IconButton size="small" onClick={() => setAnularDlg({ open: true, disposicion: d, motivo: '', saving: false })} sx={{ color: '#c62828' }}><BlockIcon fontSize="small" /></IconButton></Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog: Editar escrito */}
            <Dialog open={editDlg.open} onClose={() => setEditDlg({ open: false, disposicion: null, html: '', concepto: '', saving: false })} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: BR } }}>
                <DialogTitle sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }}>
                    Editar escrito — {editDlg.disposicion?.Formato}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, height: '70vh' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField label="Concepto" size="small" value={editDlg.concepto}
                                onChange={e => setEditDlg(p => ({ ...p, concepto: e.target.value }))} />
                            <TextField label="Contenido HTML" multiline fullWidth
                                value={editDlg.html} onChange={e => setEditDlg(p => ({ ...p, html: e.target.value }))}
                                sx={{ flex: 1, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.78rem' } }}
                                InputProps={{ sx: { height: '100%', alignItems: 'start' } }}
                                rows={25} />
                        </Box>
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: BR, overflow: 'auto', p: 2, bgcolor: '#fff' }}>
                            <Typography variant="caption" sx={{ color: '#666' }}>Vista previa:</Typography>
                            <Box sx={{ mt: 1, fontFamily: "'Times New Roman', serif" }} dangerouslySetInnerHTML={{ __html: editDlg.html }} />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDlg({ open: false, disposicion: null, html: '', concepto: '', saving: false })} sx={{ textTransform: 'none' }} disabled={editDlg.saving}>Cancelar</Button>
                    <Button onClick={guardarEdicion} variant="contained" disabled={editDlg.saving}
                        sx={{ bgcolor: '#e65100', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#bf360c' } }}>
                        {editDlg.saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Firmar */}
            <Dialog open={firmaDlg.open} onClose={() => setFirmaDlg({ open: false, disposicion: null, pin: '', saving: false, servicioOk: null })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BorderColorIcon /> Firmar disposición {firmaDlg.disposicion?.Formato}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {firmaDlg.servicioOk === false ? (
                        <Alert severity="error" sx={{ borderRadius: BR }}>
                            No se detectó el servicio local de firma (SIGIF Firma Local).
                            Verificá que esté corriendo y el token USB conectado.
                        </Alert>
                    ) : (
                        <>
                            <Alert severity="info" sx={{ mb: 2, borderRadius: BR, fontSize: '0.8rem' }}>
                                Insertá el token USB e ingresá el PIN para firmar digitalmente.
                            </Alert>
                            <TextField label="PIN del token" type="password" fullWidth autoFocus
                                value={firmaDlg.pin} onChange={e => setFirmaDlg(p => ({ ...p, pin: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') firmar(); }} sx={{ mt: 1 }} />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFirmaDlg({ open: false, disposicion: null, pin: '', saving: false, servicioOk: null })}
                        sx={{ textTransform: 'none' }} disabled={firmaDlg.saving}>Cancelar</Button>
                    {firmaDlg.servicioOk !== false && (
                        <Button onClick={firmar} variant="contained" disabled={firmaDlg.saving || !firmaDlg.pin}
                            startIcon={<BorderColorIcon />}
                            sx={{ bgcolor: '#2e7d32', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#1b5e20' } }}>
                            {firmaDlg.saving ? 'Firmando...' : 'Firmar'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Dialog: Anular */}
            <Dialog open={anularDlg.open} onClose={() => setAnularDlg({ open: false, disposicion: null, motivo: '', saving: false })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: BR } }}>
                <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }}>
                    Anular disposición {anularDlg.disposicion?.Formato}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <TextField label="Motivo" multiline rows={4} fullWidth value={anularDlg.motivo}
                        onChange={e => setAnularDlg(p => ({ ...p, motivo: e.target.value }))}
                        InputLabelProps={{ shrink: true }} sx={{ mt: 1 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAnularDlg({ open: false, disposicion: null, motivo: '', saving: false })} sx={{ textTransform: 'none' }}>Cancelar</Button>
                    <Button onClick={confirmarAnulacion} variant="contained" color="error" disabled={anularDlg.saving || !anularDlg.motivo.trim()}
                        sx={{ textTransform: 'none', borderRadius: BR }}>
                        {anularDlg.saving ? 'Anulando...' : 'Anular'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
